import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import { Link } from "react-router-dom";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

const GrnView = () => {
  useAuthRedirect();

  const [data, setData] = useState([]);
  const branch = localStorage.getItem("branch");
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // ✅ detect whether permissions are present in storage yet
  const rawPerms = useMemo(() => localStorage.getItem("permissions") || localStorage.getItem("userPermissions"), []);
  const permsLoaded = !!rawPerms;

  // ✅ Permission checks (safe even if perms not yet loaded; will be false)
  const canView  = hasPermission("GRN", "view");
  const canEntry = hasPermission("GRN", "entry");

  useEffect(() => {
    if (permsLoaded && canView) {
      fetchData();
    }
  }, [branch, permsLoaded, canView]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`https://rfs2.infraconsoft.net.in/api/grn_list?branch=${branch}`);
      setData(response.data.grnList || []);
      setFilteredData(response.data.grnList || []);
    } catch (error) {
      console.error("Error fetching grnList:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (!term) {
      setFilteredData(data);
      return;
    }

    const filtered = data.filter((item) =>
      String(item.headernumber || "").toLowerCase().includes(term) ||
      String(item.supplierMaster_name || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
  };

  // ⏳ If permissions not yet loaded, show a tiny loader instead of denying access
  if (!permsLoaded) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <h3>Loading permissions…</h3>
        </div>
      </div>
    );
  }

  // 🚫 If permissions loaded but user lacks view, block
      if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }


  // ✅ Normal render
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Goods Receipt Note</h2>
            <Link to="/inventory/grnmenu">
              <p className="back-button"><i className="fa-solid fa-backward"></i>Back</p>
            </Link>
          </div>
          <Time />
        </section>

        {/* Next step button (view is enough) */}
        {canEntry && (
        <div className="next-container">
          <Link to={`/inventory/grnInspection`}>
            <button className="next-btn">
              Next GRN Inspection<i className="fa-solid fa-arrow-right"></i>
            </button>
          </Link>
        </div>
        )}

        {/* Add New — only if entry permission */}
        {canEntry && (
          <div className="add-new-container">
            <Link to={`/inventory/addGrn`}>
              <button className="add-new-btn">
                <i className="fa-solid fa-plus"></i> Add New
              </button>
            </Link>
          </div>
        )}

        {/* Search */}
        <div className="search-filter">
          <p>Search: </p>
          <input
            type="text"
            placeholder="Search by GRN No"
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {/* Table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>GRN No</th>
                <th>GRN Date</th>
                <th>Supplier Code</th>
                <th>Supplier Name</th>
                <th>DC No</th>
                <th>DC Date</th>
                <th>Invoice No</th>
                <th>Invoice Date</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="loading-text">Loading data, please wait...</td></tr>
              ) : currentData.length > 0 ? (
                currentData.map((item, index) => (
                  <tr key={index}>
                    <td>{indexOfFirstItem + index + 1}</td>
                    <td>
                      <Link to={`/inventory/grnViewSingle/${item.headernumber}`}>
                        {item.headernumber}
                      </Link>
                    </td>
                    <td>{item.grnHeader_Curdate ? new Date(item.grnHeader_Curdate).toLocaleDateString() : ""}</td>
                    <td>{item.grnheader_suppliercode}</td>
                    <td>{item.supplierMaster_name}</td>
                    <td>{item.grnheader_DcNumber}</td>
                    <td>{item.grnheader_DcDate ? new Date(item.grnheader_DcDate).toLocaleDateString() : ""}</td>
                    <td>{item.grnheader_invoiceNumber}</td>
                    <td>{item.grnheader_invoiceDate ? new Date(item.grnheader_invoiceDate).toLocaleDateString() : ""}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="9">No Data Available</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
          <button onClick={() => paginate(1)} className={currentPage === 1 ? "active" : ""}>1</button>
          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrnView;
